import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx, resolveDir } from './primitiveUtils';

/**
 * A selectable tile: property type, amenity, contact method, request type.
 * See Apps/web/THEME.md.
 *
 * The owner's complaint was tiny icons floating in big boxes. The proportion rule
 * fixes that: label, icon and icon tile scale together, in rem, and the tile never
 * grows past what its content needs.
 *
 *   size | label     | icon      | icon tile | min height
 *   sm   | 0.875rem  | 1.25rem   | 2rem      | 2.75rem
 *   md   | 0.9375rem | 1.375rem  | 2.25rem   | 3.25rem
 *   lg   | 1rem      | 1.5rem    | 2.5rem    | 4rem
 *
 * The glyph fills about 60% of its tile (owner, 2026-10: "make the icons themselves
 * bigger, not the boxes around them"); the tiles kept their size.
 *
 * Selected = teal border + soft teal fill + the icon tile turning solid teal.
 * Hierarchy comes from border and fill, never weight (Amiri ships 400 and 700 only).
 * Checkbox tiles also show a check mark at the inline end.
 */

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onClick' | 'onSelect' | 'role' | 'type' | 'disabled' | 'className'
>;

export interface OptionTileProps extends NativeButtonProps {
  selected: boolean;
  onSelect: () => void;
  label: ReactNode;
  description?: ReactNode;
  /** A node (DomainIcon, svg) or a remix-icon class; sized by the tile. */
  icon?: ReactNode | string;
  /** row: icon tile beside the text (default). stack: icon above a centred label. */
  layout?: 'row' | 'stack';
  size?: 'sm' | 'md' | 'lg';
  /** radio (default) inside a radiogroup; checkbox for multi-select; option inside a listbox. */
  role?: 'radio' | 'checkbox' | 'option';
  disabled?: boolean;
  className?: string;
}

const SIZE = {
  sm: {
    minH: 'min-h-[2.75rem]',
    label: 'text-[0.875rem]',
    icon: 'text-[1.25rem] [&_svg]:h-5 [&_svg]:w-5',
    tile: 'h-8 w-8',
    row: 'gap-2.5 px-3 py-1.5',
    stack: 'gap-1.5 px-2 py-2.5',
  },
  md: {
    minH: 'min-h-[3.25rem]',
    label: 'text-[0.9375rem]',
    icon: 'text-[1.375rem] [&_svg]:h-[1.375rem] [&_svg]:w-[1.375rem]',
    tile: 'h-9 w-9',
    row: 'gap-3 px-3 py-2',
    stack: 'gap-2 px-2.5 py-3',
  },
  lg: {
    minH: 'min-h-[4rem]',
    label: 'text-[1rem]',
    icon: 'text-[1.5rem] [&_svg]:h-6 [&_svg]:w-6',
    tile: 'h-10 w-10',
    row: 'gap-3.5 px-4 py-2.5',
    stack: 'gap-2 px-3 py-3.5',
  },
} as const;

const OptionTile = forwardRef<HTMLButtonElement, OptionTileProps>(function OptionTile(
  {
    selected,
    onSelect,
    label,
    description,
    icon,
    layout = 'row',
    size = 'md',
    role = 'radio',
    disabled = false,
    className = '',
    ...rest
  },
  ref,
) {
  const s = SIZE[size];
  const stateAttr = role === 'option' ? { 'aria-selected': selected } : { 'aria-checked': selected };
  const isStack = layout === 'stack';

  return (
    <button
      {...rest}
      {...stateAttr}
      ref={ref}
      type="button"
      role={role}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      data-option-tile=""
      onClick={() => {
        if (!disabled) onSelect();
      }}
      className={cx(
        'relative flex w-full rounded-xl border transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25',
        'disabled:cursor-not-allowed disabled:opacity-50',
        s.minH,
        isStack ? cx('flex-col items-center justify-center text-center', s.stack) : cx('items-center text-start', s.row),
        selected
          ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] shadow-[inset_0_0_0_1px_var(--pv-color-primary)]'
          : 'border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary-light)] hover:bg-[var(--pv-color-surface-soft)] focus-visible:border-[var(--pv-color-primary)]',
        className,
      )}
    >
      {icon && (
        <span
          className={cx(
            'inline-grid shrink-0 place-items-center rounded-lg leading-none transition-colors duration-150',
            s.tile,
            s.icon,
            selected
              ? 'bg-[var(--pv-color-primary)] text-white dark:text-[var(--pv-color-surface)]'
              : 'bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]',
          )}
          aria-hidden="true"
        >
          {typeof icon === 'string' ? <i className={icon} /> : icon}
        </span>
      )}
      <span className={cx('min-w-0', !isStack && 'flex-1')}>
        <span className={cx('block leading-snug', s.label)}>{label}</span>
        {description && (
          <span className="mt-0.5 block text-[0.875rem] leading-snug text-[var(--pv-color-text-muted)]">
            {description}
          </span>
        )}
      </span>
      {role === 'checkbox' && selected && (
        <i
          className={cx(
            'ri-check-line leading-none text-[var(--pv-color-primary)]',
            s.icon,
            isStack ? 'absolute end-2 top-2' : 'shrink-0',
          )}
          aria-hidden="true"
        />
      )}
    </button>
  );
});

export default OptionTile;

export interface OptionTileGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** radiogroup: arrows move and choose. listbox: arrows move, Enter/Space choose. group: checkboxes, arrows move. */
  role?: 'radiogroup' | 'listbox' | 'group';
  /** Columns of the grid, for Up/Down. Omit for a single line. */
  columns?: number;
  children: ReactNode;
}

/**
 * Optional wrapper that gives a set of OptionTiles proper keyboard behaviour:
 * arrow keys move between tiles (Left/Right mirrored in RTL, Up/Down by `columns`),
 * Home/End jump, and in a radiogroup or listbox only one tile is a Tab stop.
 */
export function OptionTileGroup({ role = 'radiogroup', columns, children, onKeyDown, ...rest }: OptionTileGroupProps) {
  const ref = useRef<HTMLDivElement>(null);

  const tiles = () =>
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[data-option-tile]') ?? []).filter(
      (el) => !el.disabled,
    );

  // Roving tabindex for single-choice groups: the chosen tile (or the first) is the
  // one Tab stop. Re-applied after every render, since choices change.
  useLayoutEffect(() => {
    if (role === 'group') return;
    const all = tiles();
    const chosen = all.find(
      (el) => el.getAttribute('aria-checked') === 'true' || el.getAttribute('aria-selected') === 'true',
    );
    const stop = chosen ?? all[0];
    all.forEach((el) => {
      el.tabIndex = el === stop ? 0 : -1;
    });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const all = tiles();
    const current = all.findIndex((el) => el === document.activeElement);
    if (current < 0) return;
    const rtl = resolveDir(event.currentTarget) === 'rtl';
    const cols = Math.max(1, columns ?? 1);
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = current + (rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = current + (rtl ? 1 : -1);
        break;
      case 'ArrowDown':
        next = current + (columns ? cols : 1);
        break;
      case 'ArrowUp':
        next = current - (columns ? cols : 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = all.length - 1;
        break;
      default:
        return;
    }
    if (role === 'radiogroup' && !columns) next = (next + all.length) % all.length; // radios wrap
    const outOfRange = next < 0 || next >= all.length;
    // A checkbox group leaves an arrow at either end alone, so the page can still
    // scroll past it; single-choice groups keep the key (they stop at their edge).
    if (outOfRange && role === 'group') return;
    event.preventDefault();
    if (outOfRange) return;
    const target = all[next];
    target.focus();
    if (role === 'radiogroup') target.click(); // selection follows focus in a radio group
  };

  return (
    <div {...rest} ref={ref} role={role} onKeyDown={handleKeyDown}>
      {children}
    </div>
  );
}
